import * as React from 'react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import {
  FacebookSearchContentType,
  FacebookSearchSortType,
  FacebookSearchType,
  ThirdPartyPost,
  ThirdPartyProfile,
  ThirdPartyProfileSearchType,
  useGetThirdPartyProfilesLazyQuery,
  useSearchFacebookPostsLazyQuery
} from '../../../../models/graphql';
import {
  ContentFilterFacebookController,
  ContentFilterFacebookControllerProps
} from './ContentFilterFacebook.controller';
import { gql, useApolloClient } from '@apollo/client';
import { FindContentContext, SearchStepEnum } from '../FindContentProvider';
import { ScrollContext } from '../../../../providers/ScrollProvider';

export type ContentFilterFacebookApolloProps = {
  formInstance: UseFormReturn;
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  firstConnectedPageProfileId?: string;
};

gql`
  query SearchFacebookPosts(
    $socialProfileId: ID!
    $searchType: FacebookSearchType!
    $sortType: FacebookSearchSortType!
    $contentType: FacebookSearchContentType!
    $externalId: String!
    $term: String
    $cursor: String
  ) {
    searchFacebookPosts(
      socialProfileId: $socialProfileId
      searchType: $searchType
      contentType: $contentType
      sortType: $sortType
      externalId: $externalId
      term: $term
      cursor: $cursor
    ) {
      cursor
      items {
        ...ThirdPartyPostFields
      }
    }
  }

  query SearchFacebookLikedPages($socialProfileId: ID!) {
    searchFacebookLikedPages(socialProfileId: $socialProfileId) {
      ...ThirdPartyProfileFields
    }
  }
`;

export type fetchFacebookParameters = {
  socialProfileId: string;
  externalId: string;
  searchType: FacebookSearchType;
  sortType: FacebookSearchSortType;
  contentType: FacebookSearchContentType;
  term?: string;
  isExternal?: boolean;
};

export const ContentFilterFacebookApollo = (props: ContentFilterFacebookApolloProps): JSX.Element => {
  const { cache } = useApolloClient();
  const [searchParameters, setSearchParameters] = useState<fetchFacebookParameters | undefined>(undefined);
  const [searchFacebookPosts, facebookPostResults] = useSearchFacebookPostsLazyQuery({ fetchPolicy: 'network-only' });
  const [searchThirdPartyProfiles, thirdPartyProfileResults] = useGetThirdPartyProfilesLazyQuery();
  const [reachedLimit, setReachedLimit] = useState<boolean>(false);
  const [autoCompleteLoading, setAutoCompleteLoading] = useState<boolean>(false);
  const [profileSearchHistory, setProfileSearchHistory] = useState<ThirdPartyProfile[]>([]);

  const { data: posts, fetchMore } = facebookPostResults;
  const { setLoading, setLoadingMore, setPosts, setSelectedPosts, setSearchStep, setHasSearched } =
    useContext(FindContentContext);
  const { setOnScrollBottom } = useContext(ScrollContext);

  const fetchThirdPartyProfiles = useCallback(
    async (profileId: string, profileName: string) => {
      setAutoCompleteLoading(true);

      searchThirdPartyProfiles({
        variables: { socialProfileId: profileId, term: profileName, method: ThirdPartyProfileSearchType.FACEBOOK_PAGE }
      })
        .then(results => {
          if (results.error) {
            throw results.error;
          }
          const profiles: ThirdPartyProfile[] = results.data?.searchThirdPartyProfiles ?? [];

          // Make sure the history is updated on success
          setProfileSearchHistory(prevState => [...prevState, ...profiles]);
        })
        .finally(() => {
          setAutoCompleteLoading(false);
        });
    },
    [searchThirdPartyProfiles]
  );

  const fetchFacebookPosts = useCallback(
    async (parameters: fetchFacebookParameters) => {
      setLoading(true);

      await searchFacebookPosts({
        variables: parameters
      })
        .then(results => {
          if (results.error) {
            throw results.error;
          } else if (results.data?.searchFacebookPosts.items.length) {
            setSearchStep(SearchStepEnum.TWO);
          }

          setHasSearched(true);
        })
        .finally(() => setLoading(false));
    },
    [searchFacebookPosts, setHasSearched, setLoading, setSearchStep]
  );

  const purgeFacebookPostsCache = useCallback(() => {
    cache.modify({
      fields: {
        searchFacebookPosts() {
          return {
            cursor: '',
            items: []
          };
        }
      }
    });
  }, [cache]);

  const purgeFacebookCache = useCallback(() => {
    purgeFacebookPostsCache();

    setPosts([]);
    setSelectedPosts([]);
    setReachedLimit(false);
  }, [purgeFacebookPostsCache, setPosts, setSelectedPosts]);

  useEffect(() => {
    if (posts?.searchFacebookPosts.items) {
      setPosts(posts.searchFacebookPosts.items as ThirdPartyPost[]);
    }
  }, [posts, setPosts]);

  useEffect(() => {
    setOnScrollBottom(() => {
      // Don't proceed if we already reached limit
      if (!reachedLimit && !!searchParameters?.socialProfileId) {
        setLoadingMore(true);

        const cursor = posts?.searchFacebookPosts.cursor;
        fetchMore({ variables: { ...searchParameters, cursor } })
          .then(results => {
            const newItems = results.data.searchFacebookPosts.items;
            // If no results, we should not fetch more
            setReachedLimit(newItems.length === 0);
            setPosts(prev => [...prev, ...newItems]);
          })
          .finally(() => {
            setLoadingMore(false);
          });
      }
    });
  }, [fetchMore, posts, reachedLimit, searchParameters, setLoadingMore, setOnScrollBottom, setPosts]);

  const controllerProps: ContentFilterFacebookControllerProps = {
    ...props,
    searchParameters,
    setSearchParameters,
    fetchFacebookPosts,
    thirdPartyProfiles: thirdPartyProfileResults.data?.searchThirdPartyProfiles || [],
    fetchThirdPartyProfiles,
    purgeFacebookCache,
    autoCompleteLoading,
    profileSearchHistory
  };

  return <ContentFilterFacebookController {...controllerProps} />;
};
